import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { LineItemProvisioningState } from '../types/order';
import api from '../services/api';
import SupaBuildingUnitPicker, { type SupaBuildingSelection } from './SupaBuildingUnitPicker';

const SPEED_PLANS = [
  { value: 'supa-25-10',     label: '25 / 10 Mbps' },
  { value: 'supa-25-25',     label: '25 / 25 Mbps' },
  { value: 'supa-50-20',     label: '50 / 20 Mbps' },
  { value: 'supa-50-50',     label: '50 / 50 Mbps' },
  { value: 'supa-100-20',    label: '100 / 20 Mbps' },
  { value: 'supa-250-25',    label: '250 / 25 Mbps' },
  { value: 'supa-500-500',   label: '500 / 500 Mbps' },
  { value: 'supa-750-750',   label: '750 / 750 Mbps' },
  { value: 'supa-1000-1000', label: '1000 / 1000 Mbps' },
  { value: 'supa-2000-2000', label: '2000 / 2000 Mbps' },
  { value: 'supa-3000-3000', label: '3000 / 3000 Mbps' },
];

const AU_STATES = [
  { value: 'nsw', label: 'NSW' },
  { value: 'vic', label: 'VIC' },
  { value: 'qld', label: 'QLD' },
  { value: 'wa',  label: 'WA'  },
  { value: 'sa',  label: 'SA'  },
  { value: 'act', label: 'ACT' },
  { value: 'tas', label: 'TAS' },
  { value: 'nt',  label: 'NT'  },
];

const POP_OPTIONS = [
  { value: 'bdr01.sy4.nsw.sonicnetau.network', label: 'bdr01.sy4.nsw (160.236.12.17)' },
];

const NBN_TECHNOLOGY_TYPES = ['FTTP', 'FTTN', 'FTTB', 'FTTC', 'HFC', 'FW'];
const NBN_IP_ADDRESS_TYPES = ['CGNAT', 'Dynamic IP', 'Static IP'];
const NBN_MAPPING_OPTIONS = ['Default', 'Tagged'];
const NBN_UNID_PORTS = ['UNI-D1', 'UNI-D2', 'UNI-D3', 'UNI-D4'];

function extractState(address: string): string {
  const match = address.match(/\b(NSW|VIC|QLD|WA|SA|ACT|TAS|NT)\b/i);
  return match ? match[1].toLowerCase() : '';
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

type Props = {
  open: boolean;
  orderId: string;
  cartLineIndex: number;
  accountId: string | null | undefined;
  serviceAddress: string;
  serviceTypeCode: string;
  radiusUsername: string | null;
  existingAccountServiceId: string | null;
  onClose: () => void;
  onSuccess: (updated: LineItemProvisioningState) => void;
};

interface FormState {
  service_identifier: string;
  auth_type: 'IPoE' | 'PPPoE';
  pppoe_username: string;
  pppoe_password: string;
  cpe_mac_address: string;
  speed_plan: string;
  service_status: string;
  state: string;
  pop: string;
  activation_date: string;
  // supa-only: building/unit assignment, pulled from the Supa serviced-buildings API
  building_place_id: string;
  building_name: string;
  building_key: string;
  service_address: string;
  // nbn-only
  nbn_plan_id: string;
  wsm_service_id: string;
  nbn_carrier_id: string;
  nbn_avc: string;
  nbn_poi: string;
  nbn_technology_type: string;
  nbn_ip_address_type: string;
  nbn_ip_address: string;
  nbn_service_class: string;
  nbn_sla: string;
  nbn_mapping: string;
  nbn_unid_port: string;
}

const DEFAULT_FORM: FormState = {
  service_identifier: '',
  auth_type: 'IPoE',
  pppoe_username: '',
  pppoe_password: '',
  cpe_mac_address: '',
  speed_plan: 'supa-100-20',
  service_status: 'ACTIVE',
  state: '',
  pop: '',
  activation_date: todayIso(),
  building_place_id: '',
  building_name: '',
  building_key: '',
  service_address: '',
  nbn_plan_id: '',
  wsm_service_id: '',
  nbn_carrier_id: '',
  nbn_avc: '',
  nbn_poi: '',
  nbn_technology_type: '',
  nbn_ip_address_type: '',
  nbn_ip_address: '',
  nbn_service_class: '',
  nbn_sla: '',
  nbn_mapping: '',
  nbn_unid_port: '',
};

export default function AccountServiceModal({
  open,
  orderId,
  cartLineIndex,
  accountId,
  serviceAddress,
  serviceTypeCode,
  radiusUsername,
  existingAccountServiceId,
  onClose,
  onSuccess,
}: Props) {
  const isEdit = !!existingAccountServiceId;
  const isNbn = (serviceTypeCode || '').toLowerCase() === 'nbn';
  const carrier = isNbn ? 'nbn' : 'supa';
  const carrierDisplayName = isNbn ? 'NBN' : 'Supa Networks';

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nbnPlans, setNbnPlans] = useState<{ id: string; name: string }[]>([]);

  const set = (field: keyof FormState, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  useEffect(() => {
    if (!open || !isNbn) {
      setNbnPlans([]);
      return;
    }
    api.getOrderProducts({ category: 'internet', subcategory: 'nbn', type: 'internet_plan' })
      .then((products: { id: string; name: string }[]) => setNbnPlans(products.map((p) => ({ id: p.id, name: p.name }))))
      .catch(() => setNbnPlans([]));
  }, [open, isNbn]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSubmitting(false);

    const stateFromAddress = extractState(serviceAddress);
    const baseForm: FormState = {
      ...DEFAULT_FORM,
      state: stateFromAddress,
      activation_date: todayIso(),
      service_address: serviceAddress || '',
    };

    if (isEdit && existingAccountServiceId) {
      setLoading(true);
      setForm(baseForm);
      api.adminGetOrderAccountService(orderId, cartLineIndex)
        .then((data) => {
          const td = data.type_specific_data as Record<string, string>;
          setForm({
            service_identifier: data.service_id,
            auth_type: (td.auth_type as 'IPoE' | 'PPPoE') || 'IPoE',
            pppoe_username: td.pppoe_username || '',
            pppoe_password: '',
            cpe_mac_address: td.cpe_mac_address || '',
            speed_plan: td.speed_plan || 'supa-100-20',
            service_status: td.service_status || 'ACTIVE',
            state: td.state || stateFromAddress,
            pop: td.pop || '',
            activation_date: td.activation_date || todayIso(),
            building_place_id: td.building_place_id || '',
            building_name: td.building_name || '',
            building_key: td.building_key || '',
            service_address: td.service_address || serviceAddress || '',
            nbn_plan_id: data.nbn_plan_id || '',
            wsm_service_id: data.wsm_service_id || '',
            nbn_carrier_id: data.nbn_carrier_id || '',
            nbn_avc: data.nbn_avc || '',
            nbn_poi: data.nbn_poi || '',
            nbn_technology_type: data.nbn_technology_type || '',
            nbn_ip_address_type: data.nbn_ip_address_type || '',
            nbn_ip_address: data.nbn_ip_address || '',
            nbn_service_class: data.nbn_service_class || '',
            nbn_sla: data.nbn_sla || '',
            nbn_mapping: data.nbn_mapping || '',
            nbn_unid_port: data.nbn_unid_port || '',
          });
        })
        .catch((err: any) => {
          setError(err.response?.data?.detail || 'Could not load current service data — verify before saving.');
        })
        .finally(() => setLoading(false));
    } else if (radiusUsername) {
      setLoading(true);
      const newForm: FormState = { ...baseForm, service_identifier: radiusUsername };
      setForm(newForm);
      api.adminGetRadiusSubscriber(radiusUsername)
        .then((info) => {
          setForm((prev) => ({
            ...prev,
            auth_type: info.connection_type === 'pppoe' ? 'PPPoE' : 'IPoE',
            pppoe_username: info.connection_type === 'pppoe' ? radiusUsername : '',
            speed_plan: info.groupname || 'supa-100-20',
          }));
        })
        .catch(() => {
          // Non-fatal — staff can fill in manually
        })
        .finally(() => setLoading(false));
    } else {
      setForm(baseForm);
      setLoading(false);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!form.service_identifier.trim()) {
      setError('Service Identifier is required');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const type_specific_data: Record<string, string> = {
        service_identifier: form.service_identifier.trim(),
        auth_type: form.auth_type,
        service_status: form.service_status,
        carrier,
        state: form.state,
        activation_date: form.activation_date,
      };
      if (form.auth_type === 'PPPoE') {
        if (form.pppoe_username.trim()) type_specific_data.pppoe_username = form.pppoe_username.trim();
        if (form.pppoe_password.trim()) type_specific_data.pppoe_password = form.pppoe_password.trim();
      }
      if (!isNbn) {
        type_specific_data.speed_plan = form.speed_plan;
        type_specific_data.pop = form.pop;
        if (form.auth_type === 'IPoE' && form.cpe_mac_address.trim()) {
          type_specific_data.cpe_mac_address = form.cpe_mac_address.trim().toUpperCase();
        }
        if (form.building_place_id) type_specific_data.building_place_id = form.building_place_id;
        if (form.building_name) type_specific_data.building_name = form.building_name;
        if (form.building_key) type_specific_data.building_key = form.building_key;
        if (form.service_address.trim()) type_specific_data.service_address = form.service_address.trim();
      }

      const isFttpOrFw = form.nbn_technology_type === 'FTTP' || form.nbn_technology_type === 'FW';
      const nbnFields = isNbn ? {
        nbn_plan_id: form.nbn_plan_id || null,
        wsm_service_id: form.wsm_service_id.trim() || null,
        nbn_carrier_id: form.nbn_carrier_id.trim() || null,
        nbn_avc: form.nbn_avc.trim() || null,
        nbn_poi: form.nbn_poi.trim() || null,
        nbn_technology_type: form.nbn_technology_type || null,
        nbn_ip_address_type: form.nbn_ip_address_type || null,
        nbn_ip_address: form.nbn_ip_address_type === 'Static IP' ? (form.nbn_ip_address.trim() || null) : null,
        nbn_service_class: form.nbn_service_class.trim() || null,
        nbn_sla: form.nbn_sla.trim() || null,
        nbn_mapping: form.nbn_mapping || null,
        nbn_unid_port: isFttpOrFw ? (form.nbn_unid_port || null) : null,
      } : {};

      const updated = await api.adminProvisionAccountService(orderId, cartLineIndex, {
        service_id: form.service_identifier.trim(),
        type_specific_data,
        ...nbnFields,
      });
      onSuccess(updated);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to add service to account');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = form.service_identifier.trim().length > 0 && !submitting && !loading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {isEdit ? 'Edit Account Service' : 'Add Service to Account'}
      </DialogTitle>
      <DialogContent>
        {!accountId ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            This order is not linked to a CRM account. Link an account before adding a service.
          </Alert>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label="Service Identifier (SAS)"
              value={form.service_identifier}
              onChange={(e) => set('service_identifier', e.target.value)}
              fullWidth
              size="small"
              helperText="RADIUS username / remote-id (e.g. SAS0001234567)"
              disabled={isEdit}
            />

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                Authentication type
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={form.auth_type}
                onChange={(_, v) => v && set('auth_type', v)}
                size="small"
              >
                <ToggleButton value="IPoE">IPoE</ToggleButton>
                <ToggleButton value="PPPoE">PPPoE</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {form.auth_type === 'PPPoE' && (
              <>
                <TextField
                  label="PPPoE Username"
                  value={form.pppoe_username}
                  onChange={(e) => set('pppoe_username', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="PPPoE Password"
                  value={form.pppoe_password}
                  onChange={(e) => set('pppoe_password', e.target.value)}
                  fullWidth
                  size="small"
                  helperText="Included in the service active email to the customer"
                />
              </>
            )}

            {!isNbn && form.auth_type === 'IPoE' && (
              <TextField
                label="CPE MAC Address"
                value={form.cpe_mac_address}
                onChange={(e) => set('cpe_mac_address', e.target.value)}
                fullWidth
                size="small"
                placeholder="AA:BB:CC:DD:EE:FF"
                helperText="Required for live IPoE status monitoring via RouterOS"
              />
            )}

            <Divider />

            {!isNbn && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  Building &amp; service address
                </Typography>
                <SupaBuildingUnitPicker
                  value={form.service_address}
                  initialBuildingKey={form.building_key || undefined}
                  onSelect={(selection: SupaBuildingSelection) => {
                    setForm((prev) => ({
                      ...prev,
                      building_place_id: selection.building.place_id || '',
                      building_name: selection.building.buildingName,
                      building_key: selection.buildingKey,
                      service_address: selection.address,
                      state: prev.state || extractState(selection.address),
                    }));
                  }}
                  onClear={() => {
                    setForm((prev) => ({
                      ...prev,
                      building_place_id: '',
                      building_name: '',
                      building_key: '',
                      service_address: '',
                    }));
                  }}
                />
              </Box>
            )}

            {!isNbn && (
              <FormControl fullWidth size="small">
                <InputLabel>Speed plan</InputLabel>
                <Select
                  value={form.speed_plan}
                  label="Speed plan"
                  onChange={(e) => set('speed_plan', e.target.value)}
                >
                  {SPEED_PLANS.map((p) => (
                    <MenuItem key={p.value} value={p.value}>
                      {p.label}
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({p.value})
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {isNbn && (
              <>
                <FormControl fullWidth size="small">
                  <InputLabel>NBN Plan</InputLabel>
                  <Select
                    value={form.nbn_plan_id}
                    label="NBN Plan"
                    onChange={(e) => set('nbn_plan_id', e.target.value)}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {nbnPlans.map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>Technology Type</InputLabel>
                  <Select
                    value={form.nbn_technology_type}
                    label="Technology Type"
                    onChange={(e) => set('nbn_technology_type', e.target.value)}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {NBN_TECHNOLOGY_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>IP Address Type</InputLabel>
                  <Select
                    value={form.nbn_ip_address_type}
                    label="IP Address Type"
                    onChange={(e) => set('nbn_ip_address_type', e.target.value)}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {NBN_IP_ADDRESS_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {form.nbn_ip_address_type === 'Static IP' && (
                  <TextField
                    label="IP Address"
                    value={form.nbn_ip_address}
                    onChange={(e) => set('nbn_ip_address', e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="e.g. 1.2.3.4"
                  />
                )}

                <TextField
                  label="Service Class"
                  value={form.nbn_service_class}
                  onChange={(e) => set('nbn_service_class', e.target.value)}
                  fullWidth
                  size="small"
                />

                <TextField
                  label="SLA"
                  value={form.nbn_sla}
                  onChange={(e) => set('nbn_sla', e.target.value)}
                  fullWidth
                  size="small"
                />

                <FormControl fullWidth size="small">
                  <InputLabel>Mapping</InputLabel>
                  <Select
                    value={form.nbn_mapping}
                    label="Mapping"
                    onChange={(e) => set('nbn_mapping', e.target.value)}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {NBN_MAPPING_OPTIONS.map((m) => (
                      <MenuItem key={m} value={m}>{m}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {(form.nbn_technology_type === 'FTTP' || form.nbn_technology_type === 'FW') && (
                  <FormControl fullWidth size="small">
                    <InputLabel>UNID Port</InputLabel>
                    <Select
                      value={form.nbn_unid_port}
                      label="UNID Port"
                      onChange={(e) => set('nbn_unid_port', e.target.value)}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {NBN_UNID_PORTS.map((p) => (
                        <MenuItem key={p} value={p}>{p}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <TextField
                  label="WSM Service ID"
                  value={form.wsm_service_id}
                  onChange={(e) => set('wsm_service_id', e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Vocus Members Portal ID"
                />

                <TextField
                  label="NBN Carrier ID"
                  value={form.nbn_carrier_id}
                  onChange={(e) => set('nbn_carrier_id', e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="LOC000045051838"
                />

                <TextField
                  label="NBN AVC"
                  value={form.nbn_avc}
                  onChange={(e) => set('nbn_avc', e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="AVC000165144132"
                />

                <TextField
                  label="NBN POI"
                  value={form.nbn_poi}
                  onChange={(e) => set('nbn_poi', e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="2GOS"
                />

                <Divider />
              </>
            )}

            <FormControl fullWidth size="small">
              <InputLabel>Service status</InputLabel>
              <Select
                value={form.service_status}
                label="Service status"
                onChange={(e) => set('service_status', e.target.value)}
              >
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="SUSPENDED">Suspended</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>State</InputLabel>
              <Select
                value={form.state}
                label="State"
                onChange={(e) => set('state', e.target.value)}
              >
                {AU_STATES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {!isNbn && (
              <FormControl fullWidth size="small">
                <InputLabel>Point of Presence (PoP)</InputLabel>
                <Select
                  value={form.pop}
                  label="Point of Presence (PoP)"
                  onChange={(e) => set('pop', e.target.value)}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {POP_OPTIONS.map((p) => (
                    <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              label="Activation date"
              type="date"
              value={form.activation_date}
              onChange={(e) => set('activation_date', e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />

            <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, px: 2, py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Carrier: <strong>{carrierDisplayName}</strong>
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        {accountId && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit}
            startIcon={submitting ? <CircularProgress size={16} /> : null}
          >
            {submitting
              ? (isEdit ? 'Updating…' : 'Adding…')
              : (isEdit ? 'Update service' : 'Add service')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
